import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {RoundStatus, RoundType} from "data/enums";
import {first, last} from "lodash";
import {IRound} from "data/types/entities";
import {SquadUtils} from "data/utils/squad";
import {RoundUtils} from "data/utils/round";

export interface IRoundsStore {
	get getIsLoading(): boolean;
	get list(): IRound[];
	get listBySquad(): IRound[];
	get currentRound(): IRound | undefined;
	get prevRound(): IRound | undefined;
	get nextRound(): IRound | undefined;
	get selectedRoundID(): number;
	set selectedRoundID(selectedRoundID: number);

	fetchRounds(): Promise<void>;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _isLoading: boolean = false;
	@observable private _selectedRoundID: number = 0;
	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@observable private _list: IRound[] = [];

	get list() {
		return this._list.map((it) => ({
			...it,
			type: RoundUtils.isFinalRound(it) ? RoundType.POST : it.type,
		}));
	}

	get listBySquad(): IRound[] {
		return this.list.filter((round) => round.tournaments?.some(SquadUtils.filterBySquadID));
	}

	get selectedRoundID() {
		return this._selectedRoundID;
	}

	set selectedRoundID(selectedRoundID: number) {
		this._selectedRoundID = selectedRoundID;
	}

	get scheduleRounds() {
		return this.listBySquad.filter((e) => e.status === RoundStatus.Scheduled);
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get currentRound() {
		return (
			this.selectedRound || this.activeRound || first(this.scheduleRounds) || last(this.list)
		);
	}

	get prevRound() {
		const currentRoundIndex = this.listBySquad.findIndex(
			(round) => round.id === this.currentRound?.id
		);
		return this.listBySquad[currentRoundIndex - 1];
	}

	get nextRound() {
		const currentRoundIndex = this.listBySquad.findIndex(
			(round) => round.id === this.currentRound?.id
		);
		return this.listBySquad[currentRoundIndex + 1];
	}

	private get activeRound() {
		return this.listBySquad.find((e) => e.status === RoundStatus.Playing);
	}

	get selectedRound() {
		return this.listBySquad.find((round) => round.id === this._selectedRoundID);
	}

	@action
	async fetchRounds() {
		const {data} = await this._jsonProvider.rounds();

		if (Array.isArray(data)) {
			runInAction(() => {
				this._list = data;
			});
		}
	}
}
